import { type ReactNode, useState } from 'react'

import { t } from '../../../common/i18n.js'
import type { ThemePreference } from '../../../common/types/theme.js'
import type { ThemeSelectHandler } from '../../utils/theme.js'
import { getInitialsFromName } from '../../views/portal/utils/format.js'
import { ArrowUpRight, ChevronDown, Search } from '../icon/solar-icons.js'
import { Link } from '../link/link.js'
import { ThemeSwitch } from '../theme-switch/theme-switch.js'
import './portal-hero.scss'

export interface PortalHeroLink {
    label: string
    url: string
    isExternal?: boolean
    openInNewTab?: boolean
}

export interface PortalHeroTab {
    path: string
    label: string
    icon: ReactNode
    isActive: boolean
    badgeCount?: number
}

// Q12 callout content. The header no longer renders it (it will be re-homed in
// the dashboard body in a later phase), but hero-data still computes it.
export interface PortalHeroCallout {
    text: string
    ctaLabel: string
    ctaUrl: string
    deadline?: string
}

export interface PortalHeroProps {
    brandLabel: string
    homeHref: string
    tabs: PortalHeroTab[]
    quickLinks: PortalHeroLink[]
    linksLabel: string
    accountLinks: PortalHeroLink[]
    userName: string
    userFullName?: string
    userAvatar?: string
    activeLang: 'et' | 'en'
    themePreference: ThemePreference
    onSelectTheme: ThemeSelectHandler
    themeLabels: Record<ThemePreference, string>
    onLogout: () => void
    onSearchOpen: () => void
}

export const PortalHero = (props: PortalHeroProps) => {
    const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false)
    const [isLinksOpen, setIsLinksOpen] = useState(false)

    return (
        <div className="portal-hero">
            <nav className="portal-hero__nav">
                {/* Brand wordmark — links to the Hub home page */}
                <Link className="portal-hero__brand" href={props.homeHref}>
                    {props.brandLabel}
                </Link>

                {/* Main navigation tabs */}
                <div className="portal-hero__tabs">
                    {props.tabs.map((tab) => (
                        <Link
                            key={tab.path}
                            href={tab.path}
                            className="portal-hero__tab"
                            isActive={tab.isActive}
                        >
                            <span className="portal-hero__tab-icon">{tab.icon}</span>
                            {tab.label}
                            {tab.badgeCount !== undefined && tab.badgeCount > 0 && (
                                <span className="portal-hero__tab-badge">{tab.badgeCount}</span>
                            )}
                        </Link>
                    ))}
                </div>

                <div className="portal-hero__nav-right">
                    {/* Links dropdown ("Lingid") */}
                    <div className="portal-hero__links-menu">
                        <button
                            type="button"
                            className="portal-hero__links-btn"
                            onClick={() => {
                                setIsLinksOpen(!isLinksOpen)
                                setIsUserDropdownOpen(false)
                            }}
                            aria-expanded={isLinksOpen}
                        >
                            {props.linksLabel}
                            <ChevronDown size={16} aria-hidden="true" />
                        </button>

                        {isLinksOpen && (
                            <>
                                <div
                                    className="portal-hero__dropdown-backdrop"
                                    onClick={() => setIsLinksOpen(false)}
                                />
                                <div className="portal-hero__dropdown">
                                    {props.quickLinks.map((link) => (
                                        <Link
                                            key={link.label}
                                            className="portal-hero__dropdown-item"
                                            href={link.url || '#'}
                                            external
                                            target={link.openInNewTab ? '_blank' : undefined}
                                        >
                                            {link.label}
                                            {link.isExternal && (
                                                <ArrowUpRight
                                                    className="portal-hero__external-icon"
                                                    size={14}
                                                    aria-hidden="true"
                                                />
                                            )}
                                        </Link>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>

                    {/* Search button — opens the global search overlay */}
                    <button
                        type="button"
                        className="portal-hero__search-btn"
                        onClick={props.onSearchOpen}
                        aria-label={t.portalHero.search.placeholder(props.activeLang)}
                    >
                        <Search size={18} aria-hidden="true" />
                    </button>

                    {/* User menu */}
                    <div className="portal-hero__user-menu">
                        <button
                            type="button"
                            className="portal-hero__avatar-btn"
                            onClick={() => {
                                setIsUserDropdownOpen(!isUserDropdownOpen)
                                setIsLinksOpen(false)
                            }}
                            aria-expanded={isUserDropdownOpen}
                        >
                            {props.userAvatar ? (
                                <img
                                    className="portal-hero__avatar"
                                    src={props.userAvatar}
                                    alt=""
                                />
                            ) : (
                                <div className="portal-hero__avatar portal-hero__avatar--placeholder">
                                    {getInitialsFromName(props.userFullName || props.userName)}
                                </div>
                            )}
                        </button>

                        {isUserDropdownOpen && (
                            <>
                                <div
                                    className="portal-hero__dropdown-backdrop"
                                    onClick={() => setIsUserDropdownOpen(false)}
                                />
                                <div className="portal-hero__dropdown">
                                    {props.accountLinks.map((link) => (
                                        <Link
                                            key={link.label}
                                            className="portal-hero__dropdown-item"
                                            href={link.url || '#'}
                                            external={link.isExternal}
                                            target={link.openInNewTab ? '_blank' : undefined}
                                            onClick={
                                                link.isExternal
                                                    ? undefined
                                                    : () => setIsUserDropdownOpen(false)
                                            }
                                        >
                                            {link.label}
                                            {link.isExternal && (
                                                <ArrowUpRight
                                                    className="portal-hero__external-icon"
                                                    size={14}
                                                    aria-hidden="true"
                                                />
                                            )}
                                        </Link>
                                    ))}
                                    <hr className="portal-hero__dropdown-divider" />
                                    <div className="portal-hero__dropdown-theme">
                                        <span className="portal-hero__dropdown-theme-label">
                                            {t.theme.label(props.activeLang)}
                                        </span>
                                        <ThemeSwitch
                                            value={props.themePreference}
                                            onChange={props.onSelectTheme}
                                            labels={props.themeLabels}
                                            size="compact"
                                            tone="chrome"
                                        />
                                    </div>
                                    <hr className="portal-hero__dropdown-divider" />
                                    <button
                                        type="button"
                                        className="portal-hero__dropdown-item portal-hero__dropdown-item--logout"
                                        onClick={props.onLogout}
                                    >
                                        {t.portalHero.nav.logout(props.activeLang)}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </nav>
        </div>
    )
}
